/**
 * /ndt-inspection/instruments — exposure devices, survey meters, UT sets,
 * yokes, densitometers (GET /ndt-inspection/instruments).
 *
 * AN INSTRUMENT IS AN EQUIPMENT ROW WITH NDT SPECS. Add instrument either
 * attaches specs to an existing unit or creates the unit through the shared
 * equipment create and attaches to it (hooks/useNdtInspection.ts).
 *
 * Calibration status is the API's, computed against the tenant's today and
 * calibration warn window. Status and kind filter server-side; the counts are a
 * tally of the unfiltered, unpaginated list (no NDT endpoint returns a count).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AlertTriangle, Clock, Gauge, HelpCircle, Plus, Search } from 'lucide-react';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { useAuth } from '../../contexts/AuthContext';
import { displayValue } from '../../features/fieldiq-handoff/contracts';
import { useNdtInstruments } from '../../hooks/useNdtInspection';
import { queryLoadFailure } from '../../lib/load-failure';
import { canAdminNdt, daysHint, ndtCalibrationSkin, ndtKindLabel, NDT_CALIBRATION_SKIN, NDT_INSTRUMENT_KIND_LABEL } from '../../lib/ndt-inspection';
import { NDT_CALIBRATION_STATUSES, NDT_INSTRUMENT_KINDS, type NdtInstrument } from '../../types/ndt-inspection';
import { AddInstrumentDialog } from './NdtDialogs';
import { NdtBadge, NdtChip, NdtChipRow, NdtEmpty, NdtPanel, NdtRegister, ndtDay, type NdtColumn } from './NdtBits';

const isStatus = (v: string | null): v is (typeof NDT_CALIBRATION_STATUSES)[number] =>
  !!v && (NDT_CALIBRATION_STATUSES as readonly string[]).includes(v);
const isKind = (v: string | null): v is (typeof NDT_INSTRUMENT_KINDS)[number] =>
  !!v && (NDT_INSTRUMENT_KINDS as readonly string[]).includes(v);

export default function NdtInstrumentsScreen() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const statusParam = searchParams.get('status');
  const kindParam = searchParams.get('kind');
  const status = isStatus(statusParam) ? statusParam : '';
  const kind = isKind(kindParam) ? kindParam : '';
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [adding, setAdding] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setQ(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);

  const params = useMemo(() => ({ status, kind, q }), [status, kind, q]);
  const filtered = !!(status || kind || q);
  const listQuery = useNdtInstruments(params);
  const allQuery = useNdtInstruments();
  const instruments = listQuery.data?.data ?? [];
  const all = allQuery.isLoading || queryLoadFailure(allQuery) ? undefined : allQuery.data?.data;
  const countOf = (pick: (i: NdtInstrument) => boolean) => displayValue(all ? all.filter(pick).length : undefined);
  const countsLoading = allQuery.isLoading;
  const canAdd = canAdminNdt(user?.role);

  const setParam = (key: 'status' | 'kind', next: string) => {
    const p = new URLSearchParams(searchParams);
    if (next) p.set(key, next);
    else p.delete(key);
    setSearchParams(p, { replace: true });
  };

  const columns: NdtColumn<NdtInstrument>[] = [
    {
      key: 'name',
      header: 'Name / serial',
      render: (i) => (
        <>
          <p className="font-extrabold text-white">{i.name || '—'}</p>
          <p className="mt-0.5 font-mono text-xs text-fieldiq-muted">{i.serialNumber || '—'}</p>
        </>
      ),
    },
    { key: 'kind', header: 'Kind', render: (i) => ndtKindLabel(i.specs?.instrumentKind) },
    { key: 'last', header: 'Last cal', render: (i) => ndtDay(i.latestCalibration?.calibratedOn) },
    {
      key: 'due',
      header: 'Due',
      render: (i) => (
        <>
          {ndtDay(i.latestCalibration?.dueOn)}
          {daysHint(i.daysUntilDue) && <p className="mt-0.5 text-xs text-fieldiq-muted">{daysHint(i.daysUntilDue)}</p>}
        </>
      ),
    },
    { key: 'status', header: 'Calibration', render: (i) => <NdtBadge skin={ndtCalibrationSkin(i.calibrationStatus)} value={i.calibrationStatus} /> },
  ];

  return (
    <div data-testid="ndt-instruments-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow="NDT inspection"
        title="Instruments"
        onBack={() => navigate(-1)}
        backLabel="Back"
        actions={
          canAdd ? (
            <button type="button" onClick={() => setAdding(true)} className="fi-action fi-action--standard fi-action--primary">
              <Plus className="size-4" aria-hidden="true" />
              Add instrument
            </button>
          ) : undefined
        }
        stats={[
          { icon: <Gauge className="size-4" aria-hidden="true" />, label: 'Total', sub: 'Registered', tone: 'neutral', loading: countsLoading, value: countOf(() => true) },
          { icon: <Clock className="size-4" aria-hidden="true" />, label: 'Due soon', sub: 'Warn window', tone: 'accent', loading: countsLoading, value: countOf((i) => i.calibrationStatus === 'due_soon') },
          { icon: <AlertTriangle className="size-4" aria-hidden="true" />, label: 'Overdue', sub: 'Past due', tone: 'accent', loading: countsLoading, value: countOf((i) => i.calibrationStatus === 'overdue') },
          { icon: <HelpCircle className="size-4" aria-hidden="true" />, label: 'Never', sub: 'No record', tone: 'neutral', loading: countsLoading, value: countOf((i) => i.calibrationStatus === 'never') },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <NdtChipRow label="Filter by calibration status">
            <NdtChip label="All" active={!status} onClick={() => setParam('status', '')} count={countsLoading ? undefined : countOf(() => true)} />
            {NDT_CALIBRATION_STATUSES.map((s) => (
              <NdtChip key={s} label={NDT_CALIBRATION_SKIN[s].label} active={status === s} onClick={() => setParam('status', s)} count={countsLoading ? undefined : countOf((i) => i.calibrationStatus === s)} />
            ))}
          </NdtChipRow>
          <NdtChipRow label="Filter by kind">
            <NdtChip label="All kinds" active={!kind} onClick={() => setParam('kind', '')} />
            {NDT_INSTRUMENT_KINDS.map((k) => (
              <NdtChip key={k} label={NDT_INSTRUMENT_KIND_LABEL[k]} active={kind === k} onClick={() => setParam('kind', k)} count={countsLoading ? undefined : countOf((i) => i.specs?.instrumentKind === k)} />
            ))}
          </NdtChipRow>
          <label className="relative block">
            <span className="sr-only">Search name, serial, model or manufacturer</span>
            <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-fieldiq-muted" aria-hidden="true" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, serial, model or manufacturer"
              className="h-10 w-full rounded-fi-control border border-fieldiq-border bg-fieldiq-card pl-9 pr-3 text-sm text-white placeholder:text-fieldiq-muted focus:border-fi-amber focus:outline-none"
            />
          </label>

          <NdtPanel
            eyebrow="Calibration register"
            title="Instruments"
            testId="ndt-instrument-register"
            footer={
              !listQuery.isLoading && !queryLoadFailure(listQuery) && instruments.length > 0
                ? filtered
                  ? `Showing ${instruments.length} of ${countOf(() => true)} instruments`
                  : `${instruments.length} instruments`
                : undefined
            }
          >
            <NdtRegister
              query={listQuery}
              rows={instruments}
              columns={columns}
              rail={(i) => ndtCalibrationSkin(i.calibrationStatus).rail}
              onOpen={(i) => navigate(`/ndt-inspection/instruments/${i.id}`)}
              subject="instruments"
              testId="ndt-instrument"
              minWidth="min-w-[820px]"
              empty={filtered ? <NdtEmpty>No instrument matches these filters.</NdtEmpty> : <NdtEmpty>There are no NDT instruments in the register.</NdtEmpty>}
              card={(i) => (
                <>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-extrabold text-white">{i.name || '—'}</p>
                      <p className="mt-0.5 font-mono text-xs text-fieldiq-muted">{i.serialNumber || '—'}</p>
                    </div>
                    <NdtBadge skin={ndtCalibrationSkin(i.calibrationStatus)} value={i.calibrationStatus} />
                  </div>
                  <p className="mt-2 text-xs text-fieldiq-text">
                    {ndtKindLabel(i.specs?.instrumentKind)} · Due {ndtDay(i.latestCalibration?.dueOn)}
                  </p>
                </>
              )}
            />
          </NdtPanel>
        </div>
      </section>

      {canAdd && <AddInstrumentDialog open={adding} onClose={() => setAdding(false)} onAdded={(id) => navigate(`/ndt-inspection/instruments/${id}`)} />}
    </div>
  );
}
